import React, { useEffect, useState, useContext } from 'react';
import { Button, Table, Tooltip, Alert, Space, message } from 'antd';
import useCurrentShortcut from "../../hooks/useCurrentShortcut";
import {BrandContext} from "../../common/BrandContext";
import axios from "../../common/axios";
import CircularLoader from "../../common/CircularLoader";

const StudentTrailLessons = () => {
    const {loadShortcutByUrl} = useCurrentShortcut();
    const brandConfig = useContext(BrandContext);
    const [disabledButtons, setDisabledButtons] = useState({});
    const [pathwayAssignmentId, setPathwayAssignmentId] = useState();
    const [pathwayAssignment, setPathwayAssignment] = useState();
    const [pathwayUserMap, setPathwayUserMap] = useState();
    const [concepts, setConcepts] = useState([]);
    const [isLoading, setLoading] = useState(true);

    const handleHashChange = () => {
        const hash = window.location.hash.slice(1);
        const hashNumericValue = Number(hash);
        if (!isNaN(hashNumericValue) && typeof hashNumericValue === 'number') {
            setPathwayAssignmentId(hashNumericValue);
        } else {
            setPathwayAssignmentId(undefined);
        }
    };

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const queryId = params.get('pathwayAssignmentId');

        if (queryId !== null) {
            const numericQueryId = Number(queryId);
            if (!isNaN(numericQueryId)) {
                setPathwayAssignmentId(numericQueryId);
            } else {
                setPathwayAssignmentId(undefined);
            }
        }

        handleHashChange();
        window.addEventListener('hashchange', handleHashChange);
        return () => {
            window.removeEventListener('hashchange', handleHashChange);
        };
    }, []);

    useEffect(() => {
        if (pathwayAssignmentId === undefined) {
            return;
        }

        setLoading(true);
        axios
            .get('/web/ui/trail/templateConcepts', {
                params: { pathwayAssignmentId },
            })
            .then((response) => {
                if (response.data) {
                    const data = response.data;
                    setPathwayAssignment(data.pathwayAssignment);
                    setPathwayUserMap(data.pathwayUserMap);
                    setConcepts(data.concepts);
                } else {
                    message.error('Invalid data structure from server.');
                }
            })
            .catch((err) => {
                console.error(err);
                message.error('Failed to load steps data.');
            }).finally(() => {
                setLoading(false);
            });
    }, [pathwayAssignmentId]);

    const handleBack = () => {
        setDisabledButtons((prev) => ({ ...prev, back: true }));
        loadShortcutByUrl('/web/ui/view/trail/studentTrails');
    };

    const handleActionClick = (pathwayAssignmentId, conceptId, buttonId) => {
        if (disabledButtons[buttonId]) {
            return;
        }

        setDisabledButtons((prev) => ({ ...prev, [buttonId]: true }));
        loadShortcutByUrl('/trail/loadTrail.action', {
            currentPathwayAssignmentId: pathwayAssignmentId,
            conceptId: conceptId,
        });
    };

    if(isLoading) {
        return <CircularLoader/>;
    }

    if (pathwayAssignment == undefined) {
        return (
            <Space direction="horizontal" className="flexSpaceBetween">
                <Alert showIcon={true} type="error" message={`Current ${brandConfig.tasksName} is not available`}/>;
                <Tooltip title={`Back to ${brandConfig.tasksName}`}>
                    <Button
                        type="primary"
                        disabled={disabledButtons['back']}
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                </Tooltip>
            </Space>
        );
    }

    const headerText = pathwayAssignment.topic
        ? pathwayAssignment.topic.topicName
        : `Multiple ${brandConfig.conceptName}s`;

    const data = concepts
        .map((concept) => {
            const pathwayUser = pathwayUserMap[concept.conceptId];
            if (!pathwayUser) {
                return null;
            }

            let buttonLabel = '';
            if (pathwayUser.pathwayAssignmentUserState) {
                if (pathwayUser.pathwayAssignmentUserState === 'COMPLETED') {
                    buttonLabel = 'Results';
                } else if (pathwayUser.pathwayAssignmentUserState === 'STARTED' || pathwayUser.pathwayAssignmentUserState === 'PAUSED') {
                    buttonLabel = 'Resume';
                } else {
                    buttonLabel = 'Start';
                }
            } else {
                buttonLabel = 'Start';
            }

            return {
                key: concept.conceptId,
                conceptName: concept.conceptName,
                stateDescription: pathwayUser.pathwayAssignmentUserStateDescription,
                buttonLabel,
                pathwayAssignmentId: pathwayUser.pathwayAssignmentId,
                conceptId: concept.conceptId,
                pathwayUserId: pathwayUser.pathwayUserId,
                skip: pathwayUser.skip
            };
        })
        .filter((item) => item !== null);

    // Define columns for the Ant Design Table.
    const columns = [
        {
            title: 'Concept',
            dataIndex: 'conceptName',
            key: 'conceptName',
        },
        {
            title: 'Status',
            dataIndex: 'stateDescription',
            key: 'stateDescription',
            align: 'center',
        },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => {
                const buttonId = `action-${record.conceptId}`;
                return (
                    <Button
                        type="primary"
                        disabled={disabledButtons[buttonId] || record.skip}
                        onClick={() =>
                            handleActionClick(
                                record.pathwayAssignmentId,
                                record.conceptId,
                                record.pathwayUserId,
                                buttonId
                            )
                        }
                    >
                        {record.buttonLabel}
                    </Button>
                );
            },
        },
    ];

    return (
        <div>
            <div style={{ textAlign: 'right' }}>
                <Tooltip title={`Back to ${brandConfig.tasksName}`}>
                    <Button
                        type="primary"
                        disabled={disabledButtons['back']}
                        onClick={handleBack}
                    >
                        Back
                    </Button>
                </Tooltip>
            </div>
            <h2>{pathwayAssignment.pathwayAssignmentName}</h2>
            {data.length > 0 && (
                <Space direction={"vertical"} style={{ width: '100%' }}>
                    <h3>{headerText}</h3>
                    <Table
                        dataSource={data}
                        columns={columns}
                        pagination={false}
                        style={{ width: '100%' }}
                    />
                </Space>
            )}
        </div>
    );
};

export default StudentTrailLessons;
